import { useState, type ReactNode } from 'react';
import { Eraser, Layers } from 'lucide-react';
import { Button, Card, ConfirmDialog, EmptyState } from '@/components/ui';
import type { ZoneSectionProps } from '@/components/sites/dns/ZoneSections';
import { usePurgeCache } from '@/hooks/useDomains';
import { toast } from '@/store/toastStore';
import { errorText } from '@/utils/errors';

/**
 * Cloudflare's cache of the domain. 2d2s offers the full purge only; how long
 * and what Cloudflare keeps is set in Cloudflare itself.
 */
export function CacheSection({ siteId, zone, may }: ZoneSectionProps): ReactNode {
  const [purging, setPurging] = useState(false);
  const purge = usePurgeCache(siteId, zone.domainId ?? '', {
    onSuccess: () => {
      toast.success('Кэш Cloudflare сброшен');
      setPurging(false);
    },
  });

  if (!zone.cloudflare) {
    return (
      <Card flush title="Кэш" icon={Layers}>
        <EmptyState icon={Layers} message="Домен не обслуживается Cloudflare" />
      </Card>
    );
  }

  return (
    <Card title="Кэш Cloudflare" icon={Layers}>
      <div className="stack">
        <p className="text-sm" style={{ margin: 0, maxWidth: 640 }}>
          Сбросьте кэш, если после изменений сайт показывает старую версию.
        </p>
        <div>
          <Button icon={Eraser} disabled={!may} onClick={() => setPurging(true)}>
            Сбросить весь кэш {zone.apex}
          </Button>
        </div>
        <span className="field-hint">
          Доступен только полный сброс. Правила кэширования настраиваются в Cloudflare.
        </span>
      </div>

      <ConfirmDialog
        open={purging}
        title="Сбросить кэш Cloudflare"
        description={zone.apex}
        confirmLabel="Сбросить"
        busy={purge.isPending}
        error={purge.isError ? errorText(purge.error) : undefined}
        onConfirm={() => purge.mutate()}
        onClose={() => {
          purge.reset();
          setPurging(false);
        }}
      >
        <p className="field-hint">Первые минуты после сброса нагрузка на сервер будет выше.</p>
      </ConfirmDialog>
    </Card>
  );
}
